import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import {
  InventoryEventCatchUpNextButton,
  InventoryEventCatchUpPrevButton,
} from 'src/components/Listings/InventoryEventPage/InventoryEventCatchUp/InventoryEventCatchUpBarButtons';
import { useCatchUpParams } from 'src/components/Listings/InventoryEventPage/InventoryEventPage.hooks';
import { useOnSaleEventsContext } from 'src/contexts/OnSaleEventsContext/OnSaleEventsContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { ACTIVE_EVENT_IDS_2_QUERY_PARAM } from 'src/navigations/Routes/InventoryEvent/InventoryEvent.constants';
import { CatchUpModeSource } from 'src/WebApiController';

import { OnSaleEventWithMetrics } from '../../OnSaleDiscovery/OnSaleDiscoveryView.types';
import * as styles from './OnSaleEventCatchUp.css';
import { OnSaleEventCatchUpCard } from './OnSaleEventCatchUpCard';
import { OnSaleEventCatchUpCardProps } from './OnSaleEventCatchUpCard.types';
import { OnSaleEventCatchUpDropdown } from './OnSaleEventCatchUpDropdown';

export function OnSaleEventCatchUpBar() {
  const { onSaleEvents } = useOnSaleEventsContext();

  const virtuosoRef = useRef<VirtuosoHandle>(null);

  const {
    viagVirtualIdsList,
    activeViagVirtualId,
    setSearchParams,
    numberOfEvents,
  } = useCatchUpParams(CatchUpModeSource.OnSaleEvents);

  const selectedIndex = useMemo(() => {
    if (activeViagVirtualId && viagVirtualIdsList) {
      return viagVirtualIdsList.findIndex(
        (viagId) => activeViagVirtualId === viagId
      );
    }
    return 0;
  }, [activeViagVirtualId, viagVirtualIdsList]);

  const cardProps = useMemo(() => {
    const onSaleEventsById = onSaleEvents.reduce(
      (res, event) => {
        if (event.event.viagId) {
          res[event.event.viagId + ''] = event;
        }
        return res;
      },
      {} as Record<string, OnSaleEventWithMetrics>
    );
    return (viagVirtualIdsList ?? [])
      .map((virtualId, i): OnSaleEventCatchUpCardProps | undefined => {
        const onSaleEvent = onSaleEventsById[virtualId];

        if (!onSaleEvent) {
          return undefined;
        }

        return {
          event: onSaleEvent,
          onClick: () => {
            setSearchParams((prev) => {
              prev.set(ACTIVE_EVENT_IDS_2_QUERY_PARAM, virtualId);
              return prev;
            });
          },
          isActive: i === selectedIndex,
        };
      })
      .filter((props): props is OnSaleEventCatchUpCardProps => !!props);
  }, [onSaleEvents, selectedIndex, setSearchParams, viagVirtualIdsList]);

  useEffect(() => {
    if (virtuosoRef.current && selectedIndex) {
      // With virtuoso, it can happen that the selectedIndex is computed while the virtuoso is still rendering
      // Workaround to center the initial index by applying a short delay
      setTimeout(
        () =>
          virtuosoRef.current?.scrollToIndex({
            index: selectedIndex,
            behavior: 'smooth',
            align: 'center',
          }),
        100
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);

  const showDropdown = useMemo(() => {
    return cardProps.length >= 10;
  }, [cardProps.length]);

  if (isEmpty(cardProps)) return null;

  return (
    <Stack direction="column" gap="s" className={styles.root}>
      <Stack direction="row" gap="m" width="full">
        <InventoryEventCatchUpPrevButton
          source={CatchUpModeSource.OnSaleEvents}
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          <Virtuoso
            ref={virtuosoRef}
            style={{ height: '110px', overflowY: 'hidden' }}
            totalCount={cardProps.length}
            data={cardProps}
            horizontalDirection
            itemContent={(_, props) => (
              <Stack
                height="full"
                alignItems="center"
                style={{ padding: `0 ${vars.spacing.xxs}` }}
              >
                <OnSaleEventCatchUpCard {...props} />
              </Stack>
            )}
          />
        </div>

        <InventoryEventCatchUpNextButton
          source={CatchUpModeSource.OnSaleEvents}
        />
        {showDropdown && <OnSaleEventCatchUpDropdown cardProps={cardProps} />}
      </Stack>

      <div className={styles.numberContainer}>
        {selectedIndex != null && numberOfEvents && (
          <span>{`${selectedIndex + 1} / ${numberOfEvents}`}</span>
        )}
      </div>
    </Stack>
  );
}
